import humanizeDuration from 'humanize-duration';
import { z } from 'zod';

import { RAW_DURATION_OPTIONS } from '~/hooks/use-humanize-duration';

import { buildCatalogItemTranslations } from '../utils/catalog-locale';

export const catalogEnterpriseDataSchema = z.object({
  enrollable: z.boolean(),
  applicationIds: z.array(z.string()),
});

export const catalogSearchItemSchema = z
  .object({
    key: z.string(),
    slug: z.string().optional(),
    title: z.string().optional(),
    title_ja: z.string().optional(),
    title_es: z.string().optional(),
    title_de: z.string().optional(),
    title_fr: z.string().optional(),
    title_it: z.string().optional(),
    title_pt_br: z.string().optional(),
    title_pt_pt: z.string().optional(),
    title_ar: z.string().optional(),
    title_ru: z.string().optional(),
    summary: z.string().optional(),
    summary_ja: z.string().optional(),
    summary_es: z.string().optional(),
    summary_de: z.string().optional(),
    summary_fr: z.string().optional(),
    summary_it: z.string().optional(),
    summary_pt_br: z.string().optional(),
    summary_pt_pt: z.string().optional(),
    summary_ar: z.string().optional(),
    summary_ru: z.string().optional(),
    image_url: z.string().optional(),
    difficulty: z.union([z.string(), z.array(z.string())]),
    duration: z.number(),
    enterprise: catalogEnterpriseDataSchema.optional(),
    is_free_to_public: z.boolean(),
    lp_steps: z
      .object({
        assessment: z.number().optional(),
        program: z.number().optional(),
        total: z.number().optional(),
      })
      .nullable()
      .optional(),
    skill_names: z.array(z.string()),
    raw_duration: z.number().optional(),
    rating_average: z.number(),
    rating_count: z.number(),
    semantic_type: z.enum(['Degree', 'Learning_Plan', 'Part', 'Course', 'Academic_Degree']),
    schools: z.array(z.string()),
  })
  .transform((data) => {
    const SCHOOLS = [
      {
        uri: 'taxonomy:0797c703-1d76-487a-9219-b84cf74b66a5',
        name: 'data science',
      },
      {
        uri: 'taxonomy:c7075247-28e1-4047-9a11-d86f2a3143d5',
        name: 'autonomous systems',
      },
      {
        uri: 'taxonomy:b8b37a22-80bb-45fe-8c41-7d7179cc306d',
        name: 'artificial intelligence',
      },
      {
        uri: 'taxonomy:63c3498c-9e8b-4d85-bcd4-2843d519ee04',
        name: 'business',
      },
      {
        uri: 'taxonomy:d5b00600-d85f-4c4a-a493-e7ed9374be89',
        name: 'programming and development',
      },
      {
        uri: 'taxonomy:1ab68afb-1a62-434e-bffd-7d0f18975299',
        name: 'executive leadership',
      },
      {
        uri: 'taxonomy:0dba323e-c4c6-4d09-bf98-365cf420a83a',
        name: 'product management',
      },
      {
        uri: 'taxonomy:b4a305f0-00bb-4c2c-bcfd-e875edcdcd61',
        name: 'cybersecurity',
      },
      {
        uri: 'taxonomy:37efa4cd-9fb8-48ed-a3be-4fe488e3704f',
        name: 'cloud computing',
      },
      {
        uri: 'taxonomy:c16ea252-b5e1-4b2b-b56c-f953e4eff453',
        name: 'Career resources',
      },
    ];
    const targetSchoolUri = data.schools?.[0];

    return {
      key: data.key,
      slug: data.slug ?? null,
      title: data.title ?? null,
      summary: data.summary ?? null,
      translations: buildCatalogItemTranslations(data),
      image: data.image_url ?? null,
      difficultyLevel: data.difficulty ?? null,
      duration: data.duration
        ? humanizeDuration(data.duration * 60000, { largest: 1, units: ['mo', 'w', 'd', 'h', 'm'], round: true })
        : null,
      rawDuration: data.raw_duration ?? null,
      rawDurationDisplay: data.raw_duration ? humanizeDuration(data.raw_duration * 60_000, RAW_DURATION_OPTIONS) : null,
      enterprise: data.enterprise ?? null,
      isFree: data.is_free_to_public ?? null,
      lpSteps: data.lp_steps ?? null,
      skills: data.skill_names ?? [],
      reviewSummary: {
        numberOfReviews: data.rating_count ?? null,
        starsAverage: data.rating_average ?? null,
      },
      semanticType: data.semantic_type,
      schools: SCHOOLS.filter((school) => school.uri === targetSchoolUri),
    };
  });

export const catalogSearchResultsSchema = z
  .object({
    searchResult: z
      .object({
        hits: z.array(catalogSearchItemSchema),
        nbHits: z.number(),
        page: z.number(),
        nbPages: z.number(),
        hitsPerPage: z.number(),
        exhaustiveNbHits: z.boolean(),
      })
      .optional()
      .nullable(),
    error: z.string().optional(),
  })
  .transform((data) => {
    if (!data || !data.searchResult) {
      return {
        hits: [],
        numberOfHits: 0,
        page: 0,
        pageSize: 0,
        numberOfPages: 0,
        finalPage: false,
      };
    }

    const { hits, nbHits, page, nbPages, hitsPerPage, exhaustiveNbHits } = data.searchResult;

    return {
      searchResultItems: hits,
      numberOfHits: nbHits,
      page,
      pageSize: hitsPerPage,
      numberOfPages: nbPages,
      finalPage: exhaustiveNbHits,
    };
  });

export type CatalogSearchResultsResponse = z.input<typeof catalogSearchResultsSchema>;
export type CatalogSearchResults = z.infer<typeof catalogSearchResultsSchema>;
export type CatalogSearchItem = z.infer<typeof catalogSearchItemSchema>;
export type CatalogEnterpriseData = z.infer<typeof catalogEnterpriseDataSchema>;
